import { VETCAN_STUDIO_STEPS, type VetCanStudioStepId } from "./studioReadModel";

export const STUDIO_WORKSPACE_STATE_VERSION = "a22.1_v1" as const;

export const DEFAULT_STUDIO_OPERATOR_DESCRIPTION =
  "Build a premium front-desk concierge for hours, safe scheduling guidance, renewal next steps, callback capture, clean staff handoffs, and internal preview demos while preserving current public-only routing truth.";

export const STUDIO_WORKSPACE_PREVIEW_CHANNELS = [
  "chat",
  "voice",
  "outbound_voice",
] as const;

export type StudioWorkspacePreviewChannel =
  (typeof STUDIO_WORKSPACE_PREVIEW_CHANNELS)[number];

export const STUDIO_WORKSPACE_VOICE_PERSONAS = [
  {
    id: "vetcan_calm_warm_v1",
    label: "VetCan Calm Warm",
    summary: "Calm, warm, premium front-desk delivery for general internal preview.",
    bestUse:
      "Balanced founder demos, premium walkthroughs, and general internal preview rehearsals.",
    previewCatalogEntryKey: "vetcan_preview_env_default",
    previewCatalogEntryLabel: "VetCan Preview Default",
  },
  {
    id: "vetcan_clear_confident_v1",
    label: "VetCan Clear Confident",
    summary: "Clear, polished, direct phrasing for crisp preview rehearsals.",
    bestUse:
      "Operator reviews that need direct phrasing, crisp next-step language, and minimal verbal drift.",
    previewCatalogEntryKey: null,
    previewCatalogEntryLabel: null,
  },
  {
    id: "vetcan_gentle_reassuring_v1",
    label: "VetCan Gentle Reassuring",
    summary: "Gentle, reassuring language for higher-anxiety demo and sandbox moments.",
    bestUse:
      "Trust-sensitive rehearsal moments where the tone should stay especially calm and reassuring.",
    previewCatalogEntryKey: null,
    previewCatalogEntryLabel: null,
  },
  {
    id: "vetcan_confident_concierge_v1",
    label: "VetCan Confident Concierge",
    summary:
      "Composed concierge-style phrasing for premium front-desk routing and confident next steps.",
    bestUse:
      "High-touch founder demos and polished concierge walkthroughs that need confident guidance without pressure.",
    previewCatalogEntryKey: null,
    previewCatalogEntryLabel: null,
  },
  {
    id: "vetcan_high_trust_reception_v1",
    label: "VetCan High-Trust Reception",
    summary:
      "Measured receptionist tone for trust-first greetings, handoffs, and calm intake framing.",
    bestUse:
      "Greeting, transfer, and intake-safe demos where trust and stability matter more than speed.",
    previewCatalogEntryKey: null,
    previewCatalogEntryLabel: null,
  },
  {
    id: "vetcan_efficient_scheduler_v1",
    label: "VetCan Efficient Scheduler",
    summary:
      "Tight, efficient scheduling language for availability, next actions, and callback coordination.",
    bestUse:
      "Scheduling-focused demos and preview rehearsals where concise operational clarity is the main goal.",
    previewCatalogEntryKey: null,
    previewCatalogEntryLabel: null,
  },
  {
    id: "vetcan_premium_whitelabel_neutral_v1",
    label: "VetCan Premium White-Label Neutral",
    summary:
      "Polished neutral delivery for white-label demos that need premium tone without overt brand color.",
    bestUse:
      "White-label, franchise, or multi-site showcases that need a premium tone without a strongly branded personality.",
    previewCatalogEntryKey: null,
    previewCatalogEntryLabel: null,
  },
] as const;

export type StudioWorkspaceVoicePersonaId =
  (typeof STUDIO_WORKSPACE_VOICE_PERSONAS)[number]["id"];

export const STUDIO_HUMANIZATION_TOGGLE_DEFINITIONS = [
  {
    key: "warmth",
    label: "Warmth",
    detail: "Keep the opening calm and welcoming without becoming casual.",
  },
  {
    key: "concision",
    label: "Concision",
    detail: "Prefer brief next-step language over long explanations.",
  },
  {
    key: "professionalism",
    label: "Professionalism",
    detail: "Keep the tone polished, business-class, and staff-safe.",
  },
  {
    key: "closeQuality",
    label: "Close quality",
    detail: "End with a clear next step and a courteous finish.",
  },
] as const;

export type StudioHumanizationToggleKey =
  (typeof STUDIO_HUMANIZATION_TOGGLE_DEFINITIONS)[number]["key"];

export const STUDIO_READINESS_SELECTION_DEFINITIONS = [
  {
    key: "personaReviewed",
    label: "Persona draft reviewed",
    detail: "Confirm the saved persona choice and assistant brief are still correct.",
  },
  {
    key: "routingTruthConfirmed",
    label: "Routing truth acknowledged",
    detail: "Confirm owner-approved routing truth still matches the draft assumptions.",
  },
  {
    key: "previewLaneConfirmed",
    label: "Preview-only lane confirmed",
    detail: "Confirm the current draft stays inside the internal preview-only execution lane.",
  },
] as const;

export type StudioWorkspaceReadinessSelectionKey =
  (typeof STUDIO_READINESS_SELECTION_DEFINITIONS)[number]["key"];

export const STUDIO_APPROVAL_SELECTION_DEFINITIONS = [
  {
    key: "currentBlockersReviewed",
    label: "Current blockers reviewed",
    detail:
      "Confirm the current Studio blocker list has been reviewed before preparing an internal handoff package.",
  },
  {
    key: "canaryApprovalExternalConfirmed",
    label: "External rollout approval acknowledged",
    detail:
      "Confirm rollout approval remains external to Studio and is not granted here.",
  },
] as const;

export type StudioWorkspaceApprovalSelectionKey =
  (typeof STUDIO_APPROVAL_SELECTION_DEFINITIONS)[number]["key"];

export type StudioPublishApprovalChecklistItem = {
  id:
    | "previewOnlyPosture"
    | "routingTruth"
    | "currentBlockersReviewed"
    | "canaryApprovalExternalConfirmed";
  label: string;
  detail: string;
  acknowledged: boolean;
  source: "readiness" | "approval";
  readinessKey?: StudioWorkspaceReadinessSelectionKey;
  approvalKey?: StudioWorkspaceApprovalSelectionKey;
};

export type StudioWorkspaceState = {
  version: typeof STUDIO_WORKSPACE_STATE_VERSION;
  assistantBrief: {
    operatorDescription: string;
  };
  voicePersona: {
    selectedPersonaId: StudioWorkspaceVoicePersonaId;
  };
  preview: {
    selectedChannel: StudioWorkspacePreviewChannel;
  };
  humanization: Record<StudioHumanizationToggleKey, boolean>;
  deployment: {
    notes: string;
    readinessSelections: Record<StudioWorkspaceReadinessSelectionKey, boolean>;
    approvalSelections: Record<StudioWorkspaceApprovalSelectionKey, boolean>;
    approvalRecordedAt: string | null;
  };
  session: {
    lastActiveStepId: VetCanStudioStepId;
  };
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function stringOrDefault(value: unknown, fallback: string) {
  return typeof value === "string" ? value : fallback;
}

function booleanOrDefault(value: unknown, fallback: boolean) {
  return typeof value === "boolean" ? value : fallback;
}

export function createDefaultStudioWorkspaceState(): StudioWorkspaceState {
  return {
    version: STUDIO_WORKSPACE_STATE_VERSION,
    assistantBrief: {
      operatorDescription: DEFAULT_STUDIO_OPERATOR_DESCRIPTION,
    },
    voicePersona: {
      selectedPersonaId: STUDIO_WORKSPACE_VOICE_PERSONAS[0].id,
    },
    preview: {
      selectedChannel: STUDIO_WORKSPACE_PREVIEW_CHANNELS[0],
    },
    humanization: {
      warmth: true,
      concision: true,
      professionalism: true,
      closeQuality: true,
    },
    deployment: {
      notes: "",
      readinessSelections: {
        personaReviewed: false,
        routingTruthConfirmed: false,
        previewLaneConfirmed: false,
      },
      approvalSelections: {
        currentBlockersReviewed: false,
        canaryApprovalExternalConfirmed: false,
      },
      approvalRecordedAt: null,
    },
    session: {
      lastActiveStepId: VETCAN_STUDIO_STEPS[0],
    },
  };
}

export function getStudioWorkspaceVoicePersona(id: StudioWorkspaceVoicePersonaId) {
  return (
    STUDIO_WORKSPACE_VOICE_PERSONAS.find((persona) => persona.id === id) ||
    STUDIO_WORKSPACE_VOICE_PERSONAS[0]
  );
}

export function getStudioWorkspacePreviewChannelLabel(
  channel: StudioWorkspacePreviewChannel
) {
  if (channel === "voice") {
    return "Voice preview";
  }

  if (channel === "outbound_voice") {
    return "Outbound voice preview";
  }

  return "Chat preview";
}

export function getEnabledHumanizationLabels(
  humanization: StudioWorkspaceState["humanization"]
) {
  return STUDIO_HUMANIZATION_TOGGLE_DEFINITIONS.filter(
    (toggle) => humanization[toggle.key]
  ).map((toggle) => toggle.label);
}

export function getStudioPublishApprovalChecklistItems(
  state: StudioWorkspaceState
): StudioPublishApprovalChecklistItem[] {
  return [
    {
      id: "previewOnlyPosture",
      label: "Preview-only posture acknowledged",
      detail:
        "Confirm this Studio draft remains inside the internal preview lane and does not activate live clinic traffic.",
      acknowledged: state.deployment.readinessSelections.previewLaneConfirmed,
      source: "readiness",
      readinessKey: "previewLaneConfirmed",
    },
    {
      id: "routingTruth",
      label: "Routing truth acknowledged",
      detail:
        "Confirm owner-approved routing truth still matches the current Studio draft assumptions.",
      acknowledged: state.deployment.readinessSelections.routingTruthConfirmed,
      source: "readiness",
      readinessKey: "routingTruthConfirmed",
    },
    {
      id: "currentBlockersReviewed",
      label: "Current blockers reviewed",
      detail:
        "Confirm the current readiness blockers have been reviewed and are still attached to the package honestly.",
      acknowledged: state.deployment.approvalSelections.currentBlockersReviewed,
      source: "approval",
      approvalKey: "currentBlockersReviewed",
    },
    {
      id: "canaryApprovalExternalConfirmed",
      label: "Rollout approval still external",
      detail:
        "Confirm rollout approval still happens outside Studio and is not implied by this package.",
      acknowledged:
        state.deployment.approvalSelections.canaryApprovalExternalConfirmed,
      source: "approval",
      approvalKey: "canaryApprovalExternalConfirmed",
    },
  ];
}

export function sanitizeStudioWorkspaceState(input: unknown): StudioWorkspaceState {
  const defaults = createDefaultStudioWorkspaceState();
  const candidate = isRecord(input) ? input : {};
  const assistantBrief = isRecord(candidate.assistantBrief)
    ? candidate.assistantBrief
    : {};
  const voicePersona = isRecord(candidate.voicePersona) ? candidate.voicePersona : {};
  const preview = isRecord(candidate.preview) ? candidate.preview : {};
  const humanization = isRecord(candidate.humanization) ? candidate.humanization : {};
  const deployment = isRecord(candidate.deployment) ? candidate.deployment : {};
  const readinessSelections = isRecord(deployment.readinessSelections)
    ? deployment.readinessSelections
    : {};
  const approvalSelections = isRecord(deployment.approvalSelections)
    ? deployment.approvalSelections
    : {};
  const session = isRecord(candidate.session) ? candidate.session : {};

  const selectedPersonaId = STUDIO_WORKSPACE_VOICE_PERSONAS.some(
    (persona) => persona.id === voicePersona.selectedPersonaId
  )
    ? (voicePersona.selectedPersonaId as StudioWorkspaceVoicePersonaId)
    : defaults.voicePersona.selectedPersonaId;

  const selectedChannel = STUDIO_WORKSPACE_PREVIEW_CHANNELS.includes(
    preview.selectedChannel as StudioWorkspacePreviewChannel
  )
    ? (preview.selectedChannel as StudioWorkspacePreviewChannel)
    : defaults.preview.selectedChannel;

  const lastActiveStepId = VETCAN_STUDIO_STEPS.includes(
    session.lastActiveStepId as VetCanStudioStepId
  )
    ? (session.lastActiveStepId as VetCanStudioStepId)
    : defaults.session.lastActiveStepId;

  return {
    version: STUDIO_WORKSPACE_STATE_VERSION,
    assistantBrief: {
      operatorDescription: stringOrDefault(
        assistantBrief.operatorDescription,
        defaults.assistantBrief.operatorDescription
      ),
    },
    voicePersona: {
      selectedPersonaId,
    },
    preview: {
      selectedChannel,
    },
    humanization: {
      warmth: booleanOrDefault(humanization.warmth, defaults.humanization.warmth),
      concision: booleanOrDefault(
        humanization.concision,
        defaults.humanization.concision
      ),
      professionalism: booleanOrDefault(
        humanization.professionalism,
        defaults.humanization.professionalism
      ),
      closeQuality: booleanOrDefault(
        humanization.closeQuality,
        defaults.humanization.closeQuality
      ),
    },
    deployment: {
      notes: stringOrDefault(deployment.notes, defaults.deployment.notes),
      readinessSelections: {
        personaReviewed: booleanOrDefault(
          readinessSelections.personaReviewed,
          defaults.deployment.readinessSelections.personaReviewed
        ),
        routingTruthConfirmed: booleanOrDefault(
          readinessSelections.routingTruthConfirmed,
          defaults.deployment.readinessSelections.routingTruthConfirmed
        ),
        previewLaneConfirmed: booleanOrDefault(
          readinessSelections.previewLaneConfirmed,
          defaults.deployment.readinessSelections.previewLaneConfirmed
        ),
      },
      approvalSelections: {
        currentBlockersReviewed: booleanOrDefault(
          approvalSelections.currentBlockersReviewed,
          defaults.deployment.approvalSelections.currentBlockersReviewed
        ),
        canaryApprovalExternalConfirmed: booleanOrDefault(
          approvalSelections.canaryApprovalExternalConfirmed,
          defaults.deployment.approvalSelections.canaryApprovalExternalConfirmed
        ),
      },
      approvalRecordedAt:
        typeof deployment.approvalRecordedAt === "string" &&
        deployment.approvalRecordedAt.trim()
          ? deployment.approvalRecordedAt
          : defaults.deployment.approvalRecordedAt,
    },
    session: {
      lastActiveStepId,
    },
  };
}
